import React from "react"
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography} from "@mui/material"

import type {IdentifiableCustomer} from "../types"

import {SenderCheckbox} from "./SenderCheckbox"

import {colors} from "@indebted/theme/colors"

function AssignDialog({
	customer,
	senderAddress,
	addSenderEmail,
	onAddSenderEmail,
	assigning,
	onCancel,
	onConfirm,
}: {
	customer: IdentifiableCustomer | null
	senderAddress: string
	addSenderEmail: boolean
	onAddSenderEmail: (checked: boolean) => void
	assigning: boolean
	onCancel: () => void
	onConfirm: () => void
}) {
	return (
		<Dialog open={customer !== null} onClose={assigning ? undefined : onCancel} maxWidth="sm" fullWidth>
			{customer && (
				<>
					<DialogContent>
						<Box textAlign="center" mb={3} mt={1}>
							<Typography variant="h6" fontWeight={600} color={colors.endlessGalaxy["800"]}>
								Assign this message
							</Typography>
							<Typography variant="body2" color="textSecondary" mt={0.5}>
								The message is answered as if it had been identified as this customer.
							</Typography>
						</Box>

						<AssignFact label="Customer" value={customer.Name} />
						<AssignFact label="Client" value={customer.Client} />
						{customer.CustomerRef && <AssignFact label="Customer reference" value={customer.CustomerRef} />}
						{customer.DOB && <AssignFact label="Date of birth" value={customer.DOB} />}
						{customer.Locality && <AssignFact label="Location" value={customer.Locality} />}
						{customer.MatchedOn && <AssignFact label="Why it matched" value={customer.MatchedOn} />}

						{senderAddress !== "" && (
							<Box mt={2}>
								<SenderCheckbox
									address={senderAddress}
									checked={addSenderEmail}
									onChange={onAddSenderEmail}
									disabled={assigning}
								/>
							</Box>
						)}
					</DialogContent>
					<DialogActions sx={{px: 3, pb: 3, pt: 2}}>
						<Button
							onClick={onCancel}
							variant="outlined"
							disabled={assigning}
							sx={{
								flex: 1,
								borderRadius: 2,
								color: colors.warmBlue["900"],
								borderColor: colors.warmBlue["900"],
							}}
						>
							Cancel
						</Button>
						<Button
							onClick={onConfirm}
							variant="contained"
							disabled={assigning}
							startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : undefined}
							sx={{
								flex: 1,
								borderRadius: 2,
								backgroundColor: colors.warmBlue["900"],
								"&:hover": {backgroundColor: colors.warmBlue["800"]},
							}}
						>
							Assign
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	)
}

function AssignFact({label, value}: {label: string; value: string}) {
	return (
		<Box
			display="flex"
			alignItems="flex-start"
			justifyContent="space-between"
			gap={2}
			py={1.25}
			borderTop={`1px solid ${colors.gray["200"]}`}
		>
			<Typography variant="body2" color="textSecondary">
				{label}
			</Typography>
			<Typography variant="body2" textAlign="right">
				{value}
			</Typography>
		</Box>
	)
}

export {AssignDialog}
